import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import FooterSection from "../../Layout/Footer"
import { useNavigate } from "react-router-dom"
export default function PrivacyPolicy() {
  const heroRef = useRef(null)
  const navRef = useRef(null)
  const contentRef = useRef(null)
  const navigate = useNavigate()
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, []);

  const sections = [
    { id: "intro", label: "Introduction" },
    { id: "scope", label: "Scope & Applicability" },
    { id: "role", label: "Role of the Company" },
    { id: "data", label: "Personal Data Collected" },
    { id: "purpose", label: "Purpose of Processing" },
    { id: "legal", label: "Legal Basis" },
    { id: "sharing", label: "Data Sharing" },
    { id: "transfer", label: "Cross-Border Transfers" },
    { id: "ai", label: "AI & Automated Processing" },
    { id: "retention", label: "Data Retention" },
    { id: "security", label: "Data Security" },
    { id: "rights", label: "User Rights" },
    { id: "cookies", label: "Cookies" },
    { id: "thirdparty", label: "Third-Party Links" },
    { id: "children", label: "Children’s Data" },
    { id: "business", label: "Business Transfers" },
    { id: "changes", label: "Policy Changes" },
    { id: "contact", label: "Contact & Grievance" },
  ]

  const [active, setActive] = useState("intro")

  useEffect(() => {
    gsap.fromTo(heroRef.current, { opacity: 0, y: -40 }, { opacity: 1, y: 0, duration: 1 })
    gsap.fromTo(navRef.current, { opacity: 0, x: -30 }, { opacity: 1, x: 0, duration: 1, delay: 0.2 })
    gsap.fromTo(contentRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1, delay: 0.3 })
  }, [])

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
    setActive(id)
  }

  return (
    <>
      <div className="min-h-screen bg-[#0B0B0B] text-[#CFCFCF]">
        {/* HERO */}
        <section ref={heroRef} className="relative bg-gradient-to-br from-black via-[#140800] to-[#2a1200] py-28 border-b border-white/5">
          <div className="max-w-7xl mx-auto absolute top-6 left-6">
            <button onClick={() => navigate(-1)} class=" backdrop-blur-xl bg-white/10 border border-white/20 px-5 py-2.5 rounded-full text-white flex items-center gap-2 shadow-xl hover:bg-white/20 transition">
              <svg xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                role="img" class="iconify iconify--mdi text-lg" width="1em" height="1em"
                viewBox="0 0 24 24">
                <path fill="currentColor" d="M20 11v2H8l5.5 5.5l-1.42 1.42L4.16 12l7.92-7.92L13.5 5.5L8 11z"></path>
              </svg>
              Back
            </button>
          </div>
          <div className="max-w-5xl mx-auto text-center px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            {/* <p className="text-white/80 max-w-3xl mx-auto">
            This Privacy Policy describes how Remark HR collects, uses, processes,
            stores, shares, and protects personal data in accordance with UAE
            Federal Decree-Law No. 45 of 2021 (PDPL).
          </p> */}
          </div>
        </section>

        {/* CONTENT */}
        <section className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* LEFT NAV */}
          <aside ref={navRef}>
            <div className="bg-[#111111] rounded-2xl border border-white/5 p-4 sticky top-24">
              <h3 className="font-semibold mb-4">Quick Navigation</h3>
              <ul className="space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollToSection(s.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm cursor-pointer ${active === s.id
                        ? "bg-[#FF6A00] text-white shadow-[0_0_30px_rgba(255,106,0,0.35)]"
                        : "text-gray-400 hover:bg-white/5 hover:text-white"
                        }`}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* RIGHT CONTENT */}
          <main ref={contentRef} className="lg:col-span-3">
            <div className="bg-[#111111] rounded-2xl border border-white/5 p-8 space-y-14 text-sm leading-relaxed">

              <Section id="intro" title="Introduction">
                This Privacy Policy describes how Remark HR (“Platform”), owned and operated by <span className="font-bold text-white">Visko HR & IT LLC</span>, a company duly
                incorporated under the laws of the United Arab Emirates with registered operations in Dubai (“Company”, “we”, “our”, or “us”),
                collects, uses, processes, stores, shares, and protects personal data of users who access or use the Platform. This Privacy Policy
                is drafted in accordance with applicable data protection laws and regulations, including the UAE Federal Decree-Law No. 45 of
                2021 on the Protection of Personal Data (PDPL) and other applicable legal requirements. <br /><br />
                <p>BY ACCESSING OR USING THE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREED
                  TO THE PRACTICES DESCRIBED IN THIS PRIVACY POLICY.</p>
              </Section>



              <Section id="scope" title="1. Scope and Applicability">
                This Privacy Policy applies to all users of the Platform, including but not limited to Job seekers, Employers, Recruiters,
                agencies, visitors to the Platform and any individual whose personal data is processed through the Platform. This Policy
                applies to personal data collected through the website, mobile application, online forms, account registration,
                communications and interactions conducted via the Platform.
              </Section>

              <Section id="role" title="2. Role of the Company">
                The Company is a licensed recruitment agency under the laws of the United Arab Emirates and operates a technology-
                enabled recruitment platform. The Company acts as a Data Controller in respect of personal data processed for

                recruitment, placement, candidate matching, screening, and related services. Employers and recruiters act as
                independent data controllers once personal data is shared with them, and are responsible for their own compliance
                with applicable data protection laws.
              </Section>

              <Section id="data" title="3. Personal Data We Collect">
                <b>Depending on the nature of use and user category, we may collect the following personal data:</b>
                <br />
                <b>3.1 Job Seekers:</b>
                <ul className="list-disc pl-5 space-y-1">
                  <li> Full name </li>
                  <li>Email address and phone number</li>
                  <li>Educational qualifications</li>
                  <li>Employment history and experience</li>
                  <li>Skills, certifications, and resumes/CVs</li>
                  <li>Profile photograph (if uploaded)</li>
                  <li>Login credentials and account identifiers</li>
                  <li>Communications conducted through the Platform</li>
                  <li>Any information voluntarily shared by the user</li>
                </ul><br />


                <b>3.2 Employers / Recruiters:</b>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Company name and registration details</li>
                  <li>Contact person name and designation</li>
                  <li>Business email address and phone number</li>
                  <li>Job postings and recruitment-related information</li>
                  <li>Communications conducted through the Platform</li>

                </ul>
                <br />
                <b>3.3 Technical and Usage Data:</b>
                <ul className="list-disc pl-5 space-y-1">
                  <li>IP address</li>
                  <li>Device, browser, and operating system details</li>
                  <li>Login frequency and session data</li>
                  <li>Pages visited and actions taken on the Platform</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </Section>

              <Section id="purpose" title="4. Purposes of Processing">
                <b>Personal data is collected and processed for the following purposes:</b>
                <br />
                <ul className="list-disc pl-5 space-y-1">
                  <li> To create, manage, and authenticate user accounts</li>
                  <li>To facilitate job postings, applications, candidate searches, and communications</li>
                  <li>To enable interaction between job seekers and employers/recruiters</li>
                  <li>To provide Platform features, functionality, and customer support</li>
                  <li>To provide recruitment, placement, talent sourcing, and candidate matching services to employers.</li>
                  <li>To operate AI-enabled features such as matching, recommendations, and analytics</li>
                  <li>To monitor usage, improve performance, and enhance user experience</li>
                  <li>To ensure Platform security, prevent fraud, and enforce policies</li>
                  <li>To send service-related communications, notifications, and updates</li>
                  <li>To comply with legal, regulatory, or law-enforcement requirements</li>
                  <li>The Company does not sell personal data to third parties.</li>
                </ul>
              </Section>

              <Section id="legal" title="5. Legal Basis for Processing">
                <b>Personal data is processed on one or more of the following lawful bases:</b>
                <br />
                <ul className="list-disc pl-5 space-y-1">
                  <li>User consent</li>
                  <li>Performance of contractual obligations</li>
                  <li>Compliance with legal obligations</li>
                  <li>Legitimate interests relating to Platform operation, security, improvement, and fraud prevention</li>

                </ul>
              </Section>

              <Section id="sharing" title="6. Data Sharing and Disclosure">
                <b className="flex">6.1 <p>Employers receiving candidate data act as independent data controllers under applicable data protection laws.
                  When a job seeker applies for a job or makes their profile visible, relevant personal data is shared with employers
                  or recruiters. Once personal data is shared between users, the receiving user becomes independently responsible
                  for its lawful use and compliance with applicable data protection laws. The Company shall not be responsible for
                  the subsequent use, storage, or processing of such data by users.</p></b><br />
                <b className="flex">6.2<p> We may share personal data with trusted third-party service providers for Cloud hosting, analytics and
                  performance monitoring, communication and notification services, payment processing (if applicable) and AI and
                  automation tools. Such service providers process data under contractual confidentiality and security obligations.</p></b><br />

                <b className="flex">6.3 <p>Personal data may be shared within the Company’s group entities or affiliates for operational, analytical,
                  compliance, or service improvement purposes, subject to applicable data protection laws.</p></b><br />
                <b className="flex">6.4 <p>We may disclose personal data where required by law, court order, regulatory authority, or where necessary to
                  protect the rights, safety, or security of the Platform, users, or the Company.</p></b><br />
                <ul>
                  <li> </li>
                  <li></li>
                  <li></li>

                </ul>
              </Section>

              <Section id="transfer" title="7. Cross-Border Data Transfers">
                Due to the nature of Platform operations, personal data may be stored or processed outside the UAE, including in India
                or other jurisdictions, subject to appropriate safeguards and compliance with applicable data protection laws. By using
                the Platform, users acknowledge and consent to such cross-border transfers where legally permitted.
              </Section>

              <Section id="ai" title="8. Automated Processing and AI Tools">
                The Platform may use automated systems or artificial intelligence tools to analyse data for matching and
                recommendations, improve Platform functionality and user experience. Such processing does not produce legal or
                similarly significant effects on users, does not replace human decision-making and does not determine hiring outcomes.
                All recruitment decisions are made solely by employers and job seekers. Automated processing and AI-enabled tools
                are used solely to assist recruitment activities and do not produce legally binding recruitment or employment decisions,
                and are subject to human oversight.
              </Section>

              <Section id="retention" title="9. Data Retention">
                Personal data is retained only for as long as necessary to fulfil the purposes outlined in this Policy, comply with legal
                and regulatory obligations, resolve disputes and enforce agreements. Certain data may be retained after account
                deletion where required by law or for legitimate business purposes.
              </Section>

              <Section id="security" title="10. Data Security">
                We implement reasonable technical and organisational measures to protect personal data against unauthorised
                access, loss, misuse, alteration, or disclosure. While we take appropriate safeguards, no method of transmission or
                storage is completely secure. Users acknowledge and accept the inherent risks associated with electronic data
                transmission and storage.
              </Section>

              <Section id="rights" title="11. User Rights">
                Subject to applicable law, users may have the right to access their personal data, request correction or updates, request
                deletion of personal data, withdraw consent where processing is based on consent and object to certain processing
                activities. Requests may be subject to verification and legal limitations.
              </Section>

              <Section id="cookies" title="12. Cookies and Tracking">
                The Platform uses cookies and similar technologies to enable functionality, analyse usage, and improve performance.
                For further details, please refer to our Cookies Policy.
              </Section>

              <Section id="thirdparty" title="13. Third-Party Links">
                The Platform may contain links to third-party websites or services. The Company is not responsible for the privacy
                practices or content of such third parties, and users should review their policies independently.
              </Section>

              <Section id="children" title="14. Children’s Data">
                The Platform is not intended for individuals under the age of 18. The Company does not knowingly collect personal
                data of minors. Any such data identified will be deleted.            </Section>

              <Section id="business" title="15. Business Transfers">
                In the event of a merger, acquisition, restructuring, or asset transfer, personal data may be transferred as part of such
                transaction, subject to applicable data protection laws.
              </Section>

              <Section id="changes" title="16. Changes to This Policy">
                The Company reserves the right to modify or update this Privacy Policy at any time. Changes shall be effective upon
                publication on the Platform. Continued use of the Platform constitutes acceptance of the revised Policy.
              </Section>

              <Section id="contact" title="17. Contact and Grievance Redressal">
                <b>For questions, concerns, or data-related requests, users may contact:</b>
                <b>Grievance Officer / Data Protection Contact</b>
                <p className="text-white">Email: <a href="/" className="text-blue-400 font-bold">info@viskohr.ae</a></p>
                {/* <p>Address: </p> */}
                <p className="text-white">Business Hours : <span className="text-white font-bold">Mon - Fri 9:00AM - 06:00PM</span></p>
                <b>The Company shall endeavour to respond within a reasonable timeframe in accordance with applicable law.</b>
              </Section>
            </div>
          </main>
        </section>
      </div>
      <FooterSection /></>

  )
}

function Section({ id, title, children }) {
  return (
    <section id={id}>
      <h2 className="text-lg font-semibold mb-3 text-white">
        <span className="text-[#FF6A00] mr-2">◆</span>{title}
      </h2>
      <div className="text-gray-400">
        {children}
      </div>
    </section>
  )
}
